import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchZoneById, fetchZoneLogoById, updateZone } from "services/zone";
import { useAlert } from "context/alert";
import { getBase64ImageSrc } from "utils/logo";

const getZoneDetails = (response) => {
  const body = response?.data;
  const records = body?.zone_data ?? body?.data ?? body;
  return Array.isArray(records) ? records[0] : records;
};

const getLogoData = (response) => {
  const body = response?.data;
  const value =
    body?.file_data ||
    body?.logo_data ||
    body?.zone_logo ||
    body?.logo ||
    body?.data ||
    body;
  return typeof value === "string" ? value : "";
};

const ZoneLogoUpload = ({ zoneId, onClose }) => {
  const queryClient = useQueryClient();
  const { showAlert } = useAlert();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState("");
  const numericZoneId = Number(zoneId) || null;

  const { data: zoneResponse, isLoading: isLoadingZone } = useQuery(
    ["fetch-zone-logo-settings-zone", numericZoneId],
    () => fetchZoneById(numericZoneId),
    { enabled: !!numericZoneId },
  );
  const zone = useMemo(() => getZoneDetails(zoneResponse), [zoneResponse]);

  const { data: currentLogo } = useQuery(
    ["fetch-zone-logo-by-id", numericZoneId],
    () => fetchZoneLogoById(numericZoneId),
    {
      enabled: !!numericZoneId,
      staleTime: Infinity,
      cacheTime: Infinity,
      retry: false,
      select: getLogoData,
    },
  );

  const mutation = useMutation({
    mutationFn: updateZone,
    onSuccess: () => {
      queryClient.setQueryData(
        ["fetch-zone-logo-by-id", numericZoneId],
        { data: preview.split(",").pop() },
      );
      window.dispatchEvent(
        new CustomEvent("zoneLogoChanged", { detail: numericZoneId }),
      );
      showAlert("Zone logo updated successfully.", "success");
      onClose?.();
    },
    onError: (error) => {
      showAlert(
        error?.response?.data?.message || "Could not update the zone logo.",
        "danger",
        error?.response?.data?.detail,
      );
    },
  });

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedFile({
        file_name: file.name,
        file_data: String(reader.result).split(",").pop(),
      });
      setPreview(String(reader.result));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!numericZoneId || !zone || !selectedFile) return;

    mutation.mutate({
      ...zone,
      id: zone?.zone_id || zone?.id || numericZoneId,
      zone_id: zone?.zone_id || numericZoneId,
      file_name: selectedFile.file_name,
      file_data: selectedFile.file_data,
    });
  };

  const displayedLogo = preview || getBase64ImageSrc(currentLogo);

  if (!numericZoneId) {
    return <p className="mb-0">Select a zone before uploading its logo.</p>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <p className="text-muted">Upload a logo for zone {numericZoneId}.</p>
      <label className="form-label" htmlFor="navbar-zone-logo">
        Zone logo
      </label>
      <input
        id="navbar-zone-logo"
        type="file"
        accept="image/*"
        className="form-control"
        onChange={handleFileChange}
        disabled={mutation.isLoading}
      />
      {displayedLogo && (
        <div className="mt-3 d-flex justify-content-center">
          <img
            src={displayedLogo}
            alt="Current zone logo preview"
            style={{ maxWidth: "100%", maxHeight: 150, objectFit: "contain" }}
          />
        </div>
      )}
      <div className="d-flex justify-content-end gap-2 mt-4">
        <button
          type="button"
          className="btn btn-light"
          onClick={onClose}
          disabled={mutation.isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isLoadingZone || !zone || !selectedFile || mutation.isLoading}
        >
          {mutation.isLoading ? "Saving..." : "Save logo"}
        </button>
      </div>
    </form>
  );
};

export default ZoneLogoUpload;
